import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import { BudgetableUserReadModel } from "../../../../../@api";

import { applicationConfig } from "../../../../../environmentConfiguration";
import { translationKeys } from "../../../../models/app-translation-keys";
import { TableSkeleton } from "../../../Shared/LoadingSkeletons/TableSkeleton/TableSkeleton";

import "./UserTable.scss";

interface IUserTableProps {
  users: BudgetableUserReadModel[];
  isLoading: boolean;
}

const UserTable: FunctionComponent<IUserTableProps> = (props: IUserTableProps) => {
  const { t } = useTranslation();

  const dateTemplate = (key: keyof BudgetableUserReadModel, rowData: BudgetableUserReadModel) => {
    return new Date((rowData as any)[key]).toLocaleString();
  };

  const doubleNumberTemplate = (key: keyof BudgetableUserReadModel, rowData: BudgetableUserReadModel) => {
    const value = rowData[key] as number;
    return value.toFixed(2) + " €";
  };

  const userTableTranslationKeys = translationKeys.adminArea.costOverview.areas.costOverview.user;

  const userTableMemo = useMemo(() => {
    if (props.isLoading) {
      return <TableSkeleton rows={2} columns={5} />;
    }

    return (
      <div className="user-table">
        <DataTable
          value={props.users}
          paginator
          scrollable
          scrollHeight="25rem"
          rows={10}
          resizableColumns
          rowsPerPageOptions={[10, 25, 50]}
          tableStyle={{ minWidth: "100%" }}
          sortField="lastActivity"
          loading={props.isLoading}
          sortOrder={-1}
          size="small"
        >
          <Column field="id" style={{ width: "350px", minWidth: "350px" }} header={t(userTableTranslationKeys.id)} />

          <Column
            field="lastActivity"
            sortable
            header={t(userTableTranslationKeys.lastActivity)}
            body={dateTemplate.bind(this, "lastActivity")}
          />

          <Column
            field="budgetLimit"
            sortable
            header={t(userTableTranslationKeys.budgetLimit)}
            body={doubleNumberTemplate.bind(this, "budgetLimit")}
          />

          <Column
            field="currentUsage"
            sortable
            header={t(userTableTranslationKeys.currentUsage)}
            body={doubleNumberTemplate.bind(this, "currentUsage")}
          />

          {applicationConfig.featureFlags.enableTermsOfUse && (
            /* Feature Flag for Terms of Use is used here */
            <Column field="approvedGuidelineVersion" sortable header={t(userTableTranslationKeys.termsOfUseVersion)} />
          )}
        </DataTable>
      </div>
    );
  }, [props.users, props.isLoading]);

  return userTableMemo;
};

export default UserTable;
